<template>
  <div class="reports-container">
    <AppSectionHero
      title="Agreement Reports"
      subtitle="Track agreement volume, geography, department ownership, and workflow movement from live agreement records."
      :stats="heroStats"
      :show-refresh="false"
      @stat-click="selectReportStatus"
      meta-label="Range"
      :meta-value="currentRangeLabel"
    />

    <div v-if="errorMessage" class="status-banner status-banner--error">
      {{ errorMessage }}
    </div>
    <div v-else-if="loading" class="status-banner">
      Loading live agreement report data...
    </div>

    <div class="map-section">
      <GoogleMaps
        class="map-section__map"
        :markers="mapMarkers"
        :center="mapCenter"
        :zoom="2"
        map-height="100%"
        :show-header="true"
        title="Agreement Geography Map"
        subtitle="Agreement activity plotted by partner country from the current report data"
        :use-sample-markers="false"
        empty-message="No mappable agreement countries are available yet."
        @select-marker="handleMarkerSelect"
      />

      <CCard class="border-0 shadow-sm country-detail-card">
        <CCardBody class="country-detail-body">
          <div class="country-detail-head">
            <div class="country-detail-kicker">Geography Snapshot</div>
            <div class="country-detail-title">{{ selectedCountrySummary.country }}</div>
            <div class="country-detail-copy">
              {{ selectedCountrySummary.continent }}
            </div>
          </div>

          <div class="country-stat-grid">
            <div class="country-stat country-stat--total">
              <span class="country-stat__eyebrow">Portfolio</span>
              <span class="country-stat__label">Total Agreements</span>
              <strong>{{ selectedCountrySummary.total }}</strong>
            </div>
            <div class="country-stat country-stat--approved">
              <span class="country-stat__eyebrow">Completed</span>
              <span class="country-stat__label">Approved</span>
              <strong>{{ selectedCountrySummary.approved }}</strong>
            </div>
            <div class="country-stat country-stat--review">
              <span class="country-stat__eyebrow">Active</span>
              <span class="country-stat__label">Review</span>
              <strong>{{ selectedCountrySummary.inReview }}</strong>
            </div>
          </div>

          <p class="country-detail-note">
            {{ selectedCountryInsightSentence }}
          </p>
        </CCardBody>
      </CCard>
    </div>

    <div class="metric-grid">
      <div v-for="item in metricCards" :key="item.label" class="metric-card">
        <div class="metric-kicker">{{ item.kicker }}</div>
        <div class="metric-value">{{ item.value }}</div>
        <div class="metric-label">{{ item.label }}</div>
        <div class="metric-footnote">{{ item.footnote }}</div>
      </div>
    </div>

    <div class="charts-grid">
      <CCard class="panel-card border-0 shadow-sm">
        <CCardHeader class="panel-header bg-white border-0">
          <div>
            <div class="panel-title">Agreements by Continent</div>
            <div class="panel-subtitle">Where agreement activity is concentrated geographically</div>
          </div>
        </CCardHeader>
        <CCardBody class="chart-card-body pt-0">
          <CChartDoughnut
            :datasets="continentChart.datasets"
            :labels="continentChart.labels"
            :options="doughnutOptions"
          />
        </CCardBody>
      </CCard>

      <CCard class="panel-card border-0 shadow-sm">
        <CCardHeader class="panel-header bg-white border-0">
          <div>
            <div class="panel-title">Top Countries</div>
            <div class="panel-subtitle">Countries with the highest number of agreements</div>
          </div>
        </CCardHeader>
        <CCardBody class="chart-card-body pt-0">
          <CChartBar
            :datasets="countryChart.datasets"
            :labels="countryChart.labels"
            :options="barOptions"
          />
        </CCardBody>
      </CCard>

      <CCard class="panel-card border-0 shadow-sm chart-card--wide">
        <CCardHeader class="panel-header bg-white border-0">
          <div>
            <div class="panel-title">Monthly Agreement Trend</div>
            <div class="panel-subtitle">Agreement creation and submission movement over time</div>
          </div>
        </CCardHeader>
        <CCardBody class="chart-card-body chart-card-body--wide pt-0">
          <CChartLine
            :datasets="monthlyTrendChart.datasets"
            :labels="monthlyTrendChart.labels"
            :options="lineOptions"
          />
        </CCardBody>
        <div class="trend-summary">
          <div class="trend-summary__item">
            <span class="trend-summary__label">Average / month</span>
            <strong>{{ averageMonthlyAgreements }}</strong>
          </div>
          <div class="trend-summary__item">
            <span class="trend-summary__label">Peak month</span>
            <strong>{{ peakMonthLabel }}</strong>
          </div>
          <div class="trend-summary__item">
            <span class="trend-summary__label">Filtered agreements</span>
            <strong>{{ filteredAgreements.length }}</strong>
          </div>
        </div>
      </CCard>

      <CCard class="panel-card border-0 shadow-sm">
        <CCardHeader class="panel-header bg-white border-0">
          <div>
            <div class="panel-title">By Department</div>
            <div class="panel-subtitle">Departments creating the most agreements</div>
          </div>
        </CCardHeader>
        <CCardBody class="chart-card-body pt-0">
          <CChartBar
            :datasets="departmentChart.datasets"
            :labels="departmentChart.labels"
            :options="barOptions"
          />
        </CCardBody>
      </CCard>

      <CCard class="panel-card border-0 shadow-sm">
        <CCardHeader class="panel-header bg-white border-0">
          <div>
            <div class="panel-title">Workflow Stages</div>
            <div class="panel-subtitle">How agreements are distributed across the review pipeline</div>
          </div>
        </CCardHeader>
        <CCardBody class="chart-card-body pt-0">
          <CChartDoughnut
            :datasets="stageChart.datasets"
            :labels="stageChart.labels"
            :options="doughnutOptions"
          />
        </CCardBody>
      </CCard>

      <CCard class="panel-card border-0 shadow-sm">
        <CCardHeader class="panel-header bg-white border-0">
          <div>
            <div class="panel-title">Agreement Types</div>
            <div class="panel-subtitle">Current mix of agreement categories</div>
          </div>
        </CCardHeader>
        <CCardBody class="chart-card-body pt-0">
          <CChartBar
            :datasets="typeChart.datasets"
            :labels="typeChart.labels"
            :options="barOptions"
          />
        </CCardBody>
      </CCard>
    </div>

    <div class="tables-section">
      <div class="section-header">
        <div>
          <h2 class="section-title">Detailed Reports</h2>
          <span class="section-copy">Agreement reporting is grouped into operational, geographic, and department views.</span>
        </div>
        <div class="section-actions">
          <button class="action-btn secondary" @click="showDateRange = !showDateRange">
            {{ currentRangeLabel }}
          </button>
          <button class="action-btn primary" @click="exportReport">
            Export CSV
          </button>
        </div>
      </div>

      <div v-if="showDateRange" class="date-range-picker">
        <button
          v-for="option in dateRangeOptions"
          :key="option.value"
          class="range-option"
          :class="{ active: dateRange === option.value }"
          @click="setDateRange(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="tables-tabs">
        <button
          v-for="tab in reportTabs"
          :key="tab.id"
          class="table-tab"
          :class="{ active: activeReportTab === tab.id }"
          @click="activeReportTab = tab.id"
        >
          {{ tab.name }}
        </button>
      </div>

      <div v-if="activeReportTab === 'agreements'" class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Agreement</th>
              <th>Type</th>
              <th>Partner</th>
              <th>Country</th>
              <th>Department</th>
              <th>Stage</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in agreementRows" :key="item.id">
              <td>{{ item.title }}</td>
              <td>{{ item.type }}</td>
              <td>{{ item.partner }}</td>
              <td>{{ item.country }}</td>
              <td>{{ item.department }}</td>
              <td>{{ item.stage }}</td>
              <td>{{ item.submittedAt }}</td>
            </tr>
            <tr v-if="!agreementRows.length">
              <td colspan="7" class="table-empty">No agreement records found for this range.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="activeReportTab === 'geography'" class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Continent</th>
              <th>Country</th>
              <th>Total Agreements</th>
              <th>Approved</th>
              <th>Review</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in geographyRows" :key="item.continent + '-' + item.country">
              <td>{{ item.continent }}</td>
              <td>{{ item.country }}</td>
              <td>{{ item.total }}</td>
              <td>{{ item.approved }}</td>
              <td>{{ item.inReview }}</td>
            </tr>
            <tr v-if="!geographyRows.length">
              <td colspan="5" class="table-empty">No geography data is available.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="activeReportTab === 'departments'" class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Department</th>
              <th>Total Agreements</th>
              <th>Approved</th>
              <th>Review</th>
              <th>Drafts</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in departmentRows" :key="item.department">
              <td>{{ item.department }}</td>
              <td>{{ item.total }}</td>
              <td>{{ item.approved }}</td>
              <td>{{ item.inReview }}</td>
              <td>{{ item.draft }}</td>
            </tr>
            <tr v-if="!departmentRows.length">
              <td colspan="5" class="table-empty">No department report rows are available.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { CChartBar, CChartDoughnut, CChartLine } from '@coreui/vue-chartjs'
import Service from '@/service/api'
import GoogleMaps from '@/views/GoogleMaps'
import AppSectionHero from '@/projects/components/layout/AppSectionHero'

function formatNumber(value) {
  return Number(value || 0).toLocaleString()
}

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function formatMonthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function formatMonthLabel(key) {
  const parts = String(key || '').split('-')
  if (parts.length !== 2) return key
  const date = new Date(Number(parts[0]), Number(parts[1]) - 1, 1)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

function normalizeText(value, fallback) {
  const text = String(value || '').trim()
  return text || fallback
}

function buildCounter(items, resolver) {
  const counter = {}
  ;(items || []).forEach((item) => {
    const key = resolver(item) || 'Unknown'
    counter[key] = (counter[key] || 0) + 1
  })
  return counter
}

const COUNTRY_TO_CONTINENT = {
  thailand: 'Asia',
  japan: 'Asia',
  china: 'Asia',
  korea: 'Asia',
  'south korea': 'Asia',
  singapore: 'Asia',
  malaysia: 'Asia',
  indonesia: 'Asia',
  vietnam: 'Asia',
  india: 'Asia',
  taiwan: 'Asia',
  australia: 'Oceania',
  'new zealand': 'Oceania',
  usa: 'North America',
  'united states': 'North America',
  canada: 'North America',
  mexico: 'North America',
  uk: 'Europe',
  'united kingdom': 'Europe',
  england: 'Europe',
  france: 'Europe',
  germany: 'Europe',
  italy: 'Europe',
  spain: 'Europe',
  netherlands: 'Europe',
  belgium: 'Europe',
  sweden: 'Europe',
  norway: 'Europe',
  finland: 'Europe',
  denmark: 'Europe',
  switzerland: 'Europe',
  austria: 'Europe',
  portugal: 'Europe',
  ireland: 'Europe',
  poland: 'Europe',
  czechia: 'Europe',
  'czech republic': 'Europe',
  hungary: 'Europe',
  romania: 'Europe',
  greece: 'Europe',
  turkey: 'Europe',
  egypt: 'Africa',
  southafrica: 'Africa',
  'south africa': 'Africa',
  kenya: 'Africa',
  nigeria: 'Africa',
  morocco: 'Africa',
  brazil: 'South America',
  argentina: 'South America',
  chile: 'South America',
  colombia: 'South America',
  peru: 'South America'
}

const COUNTRY_TO_COORDINATES = {
  thailand: { lat: 15.87, lng: 100.9925 },
  japan: { lat: 36.2048, lng: 138.2529 },
  china: { lat: 35.8617, lng: 104.1954 },
  korea: { lat: 35.9078, lng: 127.7669 },
  'south korea': { lat: 35.9078, lng: 127.7669 },
  singapore: { lat: 1.3521, lng: 103.8198 },
  malaysia: { lat: 4.2105, lng: 101.9758 },
  indonesia: { lat: -0.7893, lng: 113.9213 },
  vietnam: { lat: 14.0583, lng: 108.2772 },
  india: { lat: 20.5937, lng: 78.9629 },
  taiwan: { lat: 23.6978, lng: 120.9605 },
  australia: { lat: -25.2744, lng: 133.7751 },
  'new zealand': { lat: -40.9006, lng: 174.886 },
  usa: { lat: 37.0902, lng: -95.7129 },
  'united states': { lat: 37.0902, lng: -95.7129 },
  canada: { lat: 56.1304, lng: -106.3468 },
  mexico: { lat: 23.6345, lng: -102.5528 },
  uk: { lat: 55.3781, lng: -3.436 },
  'united kingdom': { lat: 55.3781, lng: -3.436 },
  england: { lat: 52.3555, lng: -1.1743 },
  france: { lat: 46.2276, lng: 2.2137 },
  germany: { lat: 51.1657, lng: 10.4515 },
  italy: { lat: 41.8719, lng: 12.5674 },
  spain: { lat: 40.4637, lng: -3.7492 },
  netherlands: { lat: 52.1326, lng: 5.2913 },
  belgium: { lat: 50.5039, lng: 4.4699 },
  sweden: { lat: 60.1282, lng: 18.6435 },
  norway: { lat: 60.472, lng: 8.4689 },
  finland: { lat: 61.9241, lng: 25.7482 },
  denmark: { lat: 56.2639, lng: 9.5018 },
  switzerland: { lat: 46.8182, lng: 8.2275 },
  austria: { lat: 47.5162, lng: 14.5501 },
  portugal: { lat: 39.3999, lng: -8.2245 },
  ireland: { lat: 53.1424, lng: -7.6921 },
  poland: { lat: 51.9194, lng: 19.1451 },
  czechia: { lat: 49.8175, lng: 15.473 },
  'czech republic': { lat: 49.8175, lng: 15.473 },
  hungary: { lat: 47.1625, lng: 19.5033 },
  romania: { lat: 45.9432, lng: 24.9668 },
  greece: { lat: 39.0742, lng: 21.8243 },
  turkey: { lat: 38.9637, lng: 35.2433 },
  egypt: { lat: 26.8206, lng: 30.8025 },
  'south africa': { lat: -30.5595, lng: 22.9375 },
  southafrica: { lat: -30.5595, lng: 22.9375 },
  kenya: { lat: -0.0236, lng: 37.9062 },
  nigeria: { lat: 9.082, lng: 8.6753 },
  morocco: { lat: 31.7917, lng: -7.0926 },
  brazil: { lat: -14.235, lng: -51.9253 },
  argentina: { lat: -38.4161, lng: -63.6167 },
  chile: { lat: -35.6751, lng: -71.543 },
  colombia: { lat: 4.5709, lng: -74.2973 },
  peru: { lat: -9.19, lng: -75.0152 }
}

export default {
  name: 'ReportsPage',
  components: {
    CChartBar,
    CChartDoughnut,
    CChartLine,
    GoogleMaps,
    AppSectionHero
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      showDateRange: false,
      dateRange: '12m',
      activeStatusFilter: 'all',
      activeReportTab: 'agreements',
      selectedCountry: '',
      agreements: [],
      dateRangeOptions: [
        { value: '30d', label: 'Last 30 Days' },
        { value: '90d', label: 'Last 90 Days' },
        { value: '12m', label: 'Last 12 Months' },
        { value: 'all', label: 'All Time' }
      ],
      reportTabs: [
        { id: 'agreements', name: 'Agreements' },
        { id: 'geography', name: 'Geography' },
        { id: 'departments', name: 'Departments' }
      ],
      doughnutOptions: {
        maintainAspectRatio: false,
        responsive: true,
        legend: { position: 'bottom' }
      },
      barOptions: {
        maintainAspectRatio: false,
        responsive: true,
        legend: { display: false },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }]
        }
      },
      lineOptions: {
        maintainAspectRatio: false,
        responsive: true,
        legend: { display: false },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }]
        }
      }
    }
  },
  computed: {
    heroStats() {
      return [
        { label: 'Agreements', value: this.filteredAgreements.length, hint: 'Filtered agreement records', icon: 'cil-file', iconClass: 'app-section-stat__icon--total', clickable: true, active: this.activeStatusFilter === 'all' },
        { label: 'Approved', value: this.approvedCount, hint: 'Admin-approved agreements', icon: 'cil-check-circle', iconClass: 'app-section-stat__icon--active', clickable: true, active: this.activeStatusFilter === 'approved' },
        { label: 'Review', value: this.inReviewCount, hint: 'Still moving in workflow', icon: 'cil-clock', iconClass: 'app-section-stat__icon--attention', clickable: true, active: this.activeStatusFilter === 'in-review' }
      ]
    },
    currentRangeLabel() {
      const match = this.dateRangeOptions.find(item => item.value === this.dateRange)
      return match ? match.label : 'Date Range'
    },
    filteredAgreements() {
      return this.agreements.filter(item => this.isDateInRange(this.getAgreementDate(item)) && this.matchesStatusFilter(item))
    },
    approvedCount() {
      return this.filteredAgreements.filter(item => item.status === 'approved').length
    },
    inReviewCount() {
      return this.filteredAgreements.filter(item => item.status !== 'approved' && item.status !== 'draft').length
    },
    draftCount() {
      return this.filteredAgreements.filter(item => item.status === 'draft').length
    },
    uniqueCountries() {
      return new Set(this.filteredAgreements.map(item => item.country).filter(Boolean)).size
    },
    uniqueDepartments() {
      return new Set(this.filteredAgreements.map(item => item.department).filter(Boolean)).size
    },
    approvalRate() {
      if (!this.filteredAgreements.length) return '0%'
      return `${Math.round((this.approvedCount / this.filteredAgreements.length) * 100)}%`
    },
    metricCards() {
      return [
        { kicker: 'Agreement Volume', value: formatNumber(this.filteredAgreements.length), label: 'Total Agreements', footnote: this.currentRangeLabel },
        { kicker: 'Geography', value: formatNumber(this.uniqueCountries), label: 'Countries', footnote: 'Countries represented in current agreement data' },
        { kicker: 'Departments', value: formatNumber(this.uniqueDepartments), label: 'Active Departments', footnote: 'Departments with agreement ownership' },
        { kicker: 'Approvals', value: formatNumber(this.approvedCount), label: 'Approved Agreements', footnote: `${this.approvalRate} approval rate, ${formatNumber(this.inReviewCount)} still in review` }
      ]
    },
    continentBreakdown() {
      return buildCounter(this.filteredAgreements, item => item.continent)
    },
    continentChart() {
      const labels = Object.keys(this.continentBreakdown)
      return {
        labels,
        datasets: [{
          backgroundColor: ['#9e212f', '#bd8f44', '#8a1728', '#c27a2c', '#6f1420', '#64748b'],
          data: labels.map(label => this.continentBreakdown[label])
        }]
      }
    },
    countryBreakdown() {
      const counter = buildCounter(this.filteredAgreements, item => item.country)
      return Object.keys(counter)
        .map(label => ({ label, value: counter[label] }))
        .sort((left, right) => right.value - left.value)
        .slice(0, 8)
    },
    countryChart() {
      return {
        labels: this.countryBreakdown.map(item => item.label),
        datasets: [{
          backgroundColor: '#8c1515',
          data: this.countryBreakdown.map(item => item.value)
        }]
      }
    },
    departmentBreakdown() {
      const counter = buildCounter(this.filteredAgreements, item => item.department)
      return Object.keys(counter)
        .map(label => ({ label, value: counter[label] }))
        .sort((left, right) => right.value - left.value)
        .slice(0, 8)
    },
    departmentChart() {
      return {
        labels: this.departmentBreakdown.map(item => item.label),
        datasets: [{
          backgroundColor: '#c77f28',
          data: this.departmentBreakdown.map(item => item.value)
        }]
      }
    },
    stageBreakdown() {
      return buildCounter(this.filteredAgreements, item => item.stage)
    },
    stageChart() {
      const labels = Object.keys(this.stageBreakdown)
      return {
        labels,
        datasets: [{
          backgroundColor: ['#8c1515', '#f59e0b', '#0ea5e9', '#16a34a', '#64748b'],
          data: labels.map(label => this.stageBreakdown[label])
        }]
      }
    },
    typeBreakdown() {
      const counter = buildCounter(this.filteredAgreements, item => item.type)
      return Object.keys(counter)
        .map(label => ({ label, value: counter[label] }))
        .sort((left, right) => right.value - left.value)
    },
    typeChart() {
      return {
        labels: this.typeBreakdown.map(item => item.label),
        datasets: [{
          backgroundColor: '#7c2d12',
          data: this.typeBreakdown.map(item => item.value)
        }]
      }
    },
    monthlyTrendSeries() {
      const counter = {}
      this.filteredAgreements.forEach((item) => {
        const date = this.getAgreementDate(item)
        if (!date) return
        const key = formatMonthKey(date)
        counter[key] = (counter[key] || 0) + 1
      })
      return Object.keys(counter).sort().map(key => ({
        key,
        label: formatMonthLabel(key),
        value: counter[key]
      }))
    },
    monthlyTrendChart() {
      return {
        labels: this.monthlyTrendSeries.map(item => item.label),
        datasets: [{
          label: 'Agreements',
          backgroundColor: 'rgba(140, 21, 21, 0.12)',
          borderColor: 'rgba(140, 21, 21, 0.95)',
          pointBackgroundColor: '#8c1515',
          data: this.monthlyTrendSeries.map(item => item.value),
          fill: true
        }]
      }
    },
    averageMonthlyAgreements() {
      if (!this.monthlyTrendSeries.length) return '0'
      const total = this.monthlyTrendSeries.reduce((sum, item) => sum + item.value, 0)
      return (total / this.monthlyTrendSeries.length).toFixed(1)
    },
    peakMonthLabel() {
      if (!this.monthlyTrendSeries.length) return '-'
      const top = this.monthlyTrendSeries.reduce((best, item) => (!best || item.value > best.value ? item : best), null)
      return top ? `${top.label} (${top.value})` : '-'
    },
    agreementRows() {
      return this.filteredAgreements
        .slice()
        .sort((left, right) => {
          const leftTime = this.getAgreementDate(left) ? this.getAgreementDate(left).getTime() : 0
          const rightTime = this.getAgreementDate(right) ? this.getAgreementDate(right).getTime() : 0
          return rightTime - leftTime
        })
        .slice(0, 20)
    },
    geographyRows() {
      const rows = {}
      this.filteredAgreements.forEach((item) => {
        const key = `${item.continent}::${item.country}`
        if (!rows[key]) {
          rows[key] = {
            continent: item.continent,
            country: item.country,
            total: 0,
            approved: 0,
            inReview: 0
          }
        }
        rows[key].total += 1
        if (item.status === 'approved') rows[key].approved += 1
        if (item.status !== 'approved' && item.status !== 'draft') rows[key].inReview += 1
      })
      return Object.values(rows).sort((left, right) => right.total - left.total).slice(0, 20)
    },
    departmentRows() {
      const rows = {}
      this.filteredAgreements.forEach((item) => {
        const key = item.department
        if (!rows[key]) {
          rows[key] = {
            department: item.department,
            total: 0,
            approved: 0,
            inReview: 0,
            draft: 0
          }
        }
        rows[key].total += 1
        if (item.status === 'approved') rows[key].approved += 1
        else if (item.status === 'draft') rows[key].draft += 1
        else rows[key].inReview += 1
      })
      return Object.values(rows).sort((left, right) => right.total - left.total).slice(0, 20)
    },
    mapMarkers() {
      return this.geographyRows
        .map((item) => {
          const coordinates = COUNTRY_TO_COORDINATES[String(item.country || '').trim().toLowerCase()]
          if (!coordinates) return null
          return {
            id: `country-${item.country}`,
            position: coordinates,
            label: String(item.total),
            title: item.country,
            meta: `${item.continent} • ${item.total} agreements • ${item.approved} approved`,
            country: item.country,
            continent: item.continent,
            total: item.total,
            approved: item.approved,
            inReview: item.inReview,
            link: ''
          }
        })
        .filter(Boolean)
    },
    mapCenter() {
      if (!this.mapMarkers.length) return { lat: 13.7563, lng: 100.5018 }
      const totals = this.mapMarkers.reduce((sum, marker) => ({
        lat: sum.lat + marker.position.lat,
        lng: sum.lng + marker.position.lng
      }), { lat: 0, lng: 0 })
      return {
        lat: totals.lat / this.mapMarkers.length,
        lng: totals.lng / this.mapMarkers.length
      }
    },
    selectedCountrySummary() {
      const fallback = this.geographyRows[0] || {
        country: 'No country selected',
        continent: 'No data',
        total: 0,
        approved: 0,
        inReview: 0
      }
      if (!this.selectedCountry) return fallback
      return this.geographyRows.find(item => item.country === this.selectedCountry) || fallback
    },
    selectedCountryCoverage() {
      if (!this.filteredAgreements.length) return '0%'
      return `${Math.round((Number(this.selectedCountrySummary.total || 0) / this.filteredAgreements.length) * 100)}%`
    },
    selectedCountryApprovalRate() {
      const total = Number(this.selectedCountrySummary.total || 0)
      if (!total) return '0%'
      return `${Math.round((Number(this.selectedCountrySummary.approved || 0) / total) * 100)}%`
    },
    selectedCountryInsightSentence() {
      return `${this.selectedCountrySummary.country} accounts for ${this.selectedCountryCoverage} of agreements in ${this.currentRangeLabel}.`
    }
  },
  mounted() {
    this.fetchLiveData()
    window.addEventListener('focus', this.fetchLiveData)
  },
  beforeDestroy() {
    window.removeEventListener('focus', this.fetchLiveData)
  },
  methods: {
    matchesStatusFilter(item) {
      if (this.activeStatusFilter === 'all') return true
      if (this.activeStatusFilter === 'approved') return item.status === 'approved'
      if (this.activeStatusFilter === 'in-review') return item.status !== 'approved' && item.status !== 'draft'
      return true
    },
    selectReportStatus(stat) {
      const label = String(stat && stat.label ? stat.label : '').toLowerCase()
      const nextStatus = {
        agreements: 'all',
        approved: 'approved',
        'in review': 'in-review'
      }[label] || 'all'
      this.activeStatusFilter = this.activeStatusFilter === nextStatus ? 'all' : nextStatus
    },
    setDateRange(value) {
      this.dateRange = value
      this.showDateRange = false
    },
    handleMarkerSelect(marker) {
      this.selectedCountry = String(marker && marker.country ? marker.country : '')
    },
    normalizeCountry(value) {
      return normalizeText(value, 'Unknown')
    },
    normalizeDepartment(value) {
      return normalizeText(value, 'International Affairs')
    },
    normalizeTitle(value) {
      return normalizeText(value, 'Untitled agreement')
    },
    normalizePartner(value) {
      return normalizeText(value, 'Partner institution')
    },
    normalizeType(value) {
      return normalizeText(value, 'Agreement')
    },
    inferContinent(country) {
      const key = String(country || '').trim().toLowerCase()
      return COUNTRY_TO_CONTINENT[key] || 'Other / Unknown'
    },
    getAgreementDate(item) {
      const value = item && (item.submittedAt || item.startDate || item.createdAt || item.endDate)
      if (!value) return null
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? null : date
    },
    isDateInRange(date) {
      if (!date || this.dateRange === 'all') return true
      const now = new Date()
      let compareFrom = null
      if (this.dateRange === '30d') compareFrom = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000))
      if (this.dateRange === '90d') compareFrom = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000))
      if (this.dateRange === '12m') compareFrom = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
      return compareFrom ? date >= compareFrom : true
    },
    mapAgreement(item) {
      const country = this.normalizeCountry(item && item.country)
      const status = String(item && item.status ? item.status : 'draft').toLowerCase()
      return {
        id: String(item && item.id ? item.id : ''),
        title: this.normalizeTitle(item && item.title),
        type: this.normalizeType(item && item.activityType ? item.activityType : item && item.type),
        partner: this.normalizePartner(item && item.partner),
        country,
        continent: this.inferContinent(country),
        department: this.normalizeDepartment(item && item.department),
        stage: normalizeText(item && item.derivedStageLabel ? String(item.derivedStageLabel) : (item && item.stageLabel ? String(item.stageLabel) : (item && item.status ? String(item.status) : '')), 'Draft'),
        status,
        submittedAt: formatDate(item && (item.submittedAt || item.startDate || item.createdAt)),
        submittedAtValue: this.getAgreementDate(item)
      }
    },
    async fetchLiveData() {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await Service.adminReview('agreements')
        const records = response && response.data && Array.isArray(response.data.data) ? response.data.data : []
        this.agreements = records.map(this.mapAgreement)
        this.selectedCountry = this.geographyRows.length ? this.geographyRows[0].country : ''
      } catch (error) {
        this.agreements = []
        this.selectedCountry = ''
        this.errorMessage = 'Unable to load agreement report data right now.'
      } finally {
        this.loading = false
      }
    },
    buildCsv(rows) {
      return rows.map((row) => row.map((value) => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`).join(',')).join('\n')
    },
    downloadCsv(filename, content) {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },
    exportReport() {
      const rows = [
        ['Agreement', 'Type', 'Partner', 'Country', 'Continent', 'Department', 'Stage', 'Submitted']
      ].concat(this.filteredAgreements.map(item => [
        item.title,
        item.type,
        item.partner,
        item.country,
        item.continent,
        item.department,
        item.stage,
        item.submittedAt
      ]))
      this.downloadCsv('agreement-reports.csv', this.buildCsv(rows))
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("reports-container");
@include workspace-button-system;

.reports-container {
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
}

.map-section {
  align-items: stretch;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 420px);
  margin-bottom: 28px;
}

.map-section__map {
  height: 100%;
  min-height: 280px;
  border-radius: 20px;
  overflow: hidden;
}

.map-section__map :deep(.map-shell) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.map-section__map :deep(.card-body) {
  flex: 1 1 auto;
}

.country-detail-card {
  background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
  border: 1px solid #e6ebf2;
  border-radius: 20px;
  box-shadow: 0 14px 34px rgba(32, 52, 84, 0.08);
  height: 100%;
  margin-top: 0;
  overflow: hidden;
}

.country-detail-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
  justify-content: flex-start;
  height: 100%;
  min-height: 100%;
  padding: 24px;
}

.country-detail-head {
  display: grid;
  gap: 6px;
}

.country-detail-kicker {
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.country-detail-title {
  color: #1f2937;
  font-size: 1.65rem;
  font-weight: 700;
  margin: 4px 0 8px 0;
}

.country-detail-copy {
  color: #6b7280;
  font-size: 0.95rem;
}

.country-stat-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 4px;
  width: 100%;
}

.country-detail-note {
  color: #6b7280;
  font-size: 0.92rem;
  line-height: 1.5;
  margin: auto 0 0;
}

.country-stat {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(71, 85, 105, 0.07);
  padding: 18px 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.country-stat:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 28px rgba(71, 85, 105, 0.12);
}

.country-stat--total {
  border-top: 4px solid #475569;
}

.country-stat--approved {
  border-top: 4px solid #15803d;
}

.country-stat--review {
  border-top: 4px solid #9e212f;
}

.country-stat__eyebrow {
  color: #94a3b8;
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.country-stat__label {
  color: #4b5563;
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 8px;
}

.country-stat strong {
  color: #111827;
  display: block;
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 8px 0;
}

@media (max-width: 1024px) {
  .map-section {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .country-stat-grid {
    gap: 12px;
    grid-template-columns: 1fr;
  }

  .country-detail-body {
    padding: 20px;
  }
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.section-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.action-btn {
  border-radius: 12px;
  min-height: 42px;
  font-weight: 700;
  padding: 12px 16px;
}

.action-btn.primary {
  background: #9e212f;
  border: 1px solid #9e212f;
  box-shadow: 0 12px 24px rgba(158, 33, 47, 0.18);
  color: #fff;
}

.action-btn.primary:hover {
  background: #8a1728;
  border-color: #8a1728;
}

.action-btn.secondary {
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #344767;
}

.action-btn.secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.date-range-picker {
  align-items: center;
  background: linear-gradient(180deg, #fffdf8, #fff7ed);
  border: 1px solid #f4d9b8;
  border-radius: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
  padding: 12px;
  position: relative;
}

.date-range-picker::before {
  color: #9a3412;
  content: "Filter Range";
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-right: 6px;
  text-transform: uppercase;
}

.range-option {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #f1dcc5;
  border-radius: 12px;
  box-shadow: 0 6px 14px rgba(154, 52, 18, 0.06);
  color: #7c2d12;
  cursor: pointer;
  font-weight: 700;
  min-width: 128px;
  padding: 11px 16px;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background 0.18s ease;
}

.range-option:hover {
  border-color: #f3bf91;
  box-shadow: 0 10px 18px rgba(154, 52, 18, 0.1);
  transform: translateY(-1px);
}

.range-option.active {
  background: linear-gradient(135deg, #8c1515, #b45309);
  border-color: transparent;
  box-shadow: 0 12px 22px rgba(140, 21, 21, 0.18);
  color: #fff;
}

.status-banner {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 14px;
  color: #9a3412;
  margin-bottom: 20px;
  padding: 14px 16px;
}

.status-banner--error {
  background: #fff1f2;
  border-color: #fecdd3;
  color: #be123c;
}

.metric-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 20px;
}

.metric-card {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98));
  border: 1px solid #e7edf4;
  border-radius: 18px;
  box-shadow: 0 14px 30px rgba(44, 52, 71, 0.06);
  padding: 18px;
}

.metric-kicker {
  color: #8c1515;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.metric-value {
  color: #243b53;
  font-size: 2rem;
  font-weight: 700;
  margin-top: 8px;
}

.metric-label {
  color: #344767;
  font-weight: 700;
  margin-top: 10px;
}

.metric-footnote {
  color: #718096;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-top: 6px;
}

.charts-grid {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: 32px;
}

.chart-card--wide {
  grid-column: 1 / -1;
}

.panel-card {
  background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  height: auto;
  overflow: hidden;
}

.panel-header {
  padding: 20px 24px 12px 24px;
}

.panel-title {
  color: #243b53;
  font-size: 1.1rem;
  font-weight: 700;
}

.panel-subtitle {
  color: #718096;
  font-size: 0.85rem;
  margin-top: 4px;
}

.chart-card-body {
  align-items: stretch;
  display: flex;
  flex: 1;
  justify-content: stretch;
  min-height: 250px;
  padding: 12px 18px 18px 18px;
}

.chart-card-body--wide {
  min-height: 300px;
}

.chart-card-body > * {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}

.chart-card-body canvas {
  height: 100% !important;
  width: 100% !important;
}

.trend-summary {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-top: 1px solid #f0f2f5;
  margin-top: 8px;
  padding: 0 24px 24px 24px;
}

.trend-summary__item {
  background: #f8fafc;
  border: 1px solid #e6ebf1;
  border-radius: 14px;
  padding: 16px;
  text-align: center;
}

.trend-summary__label {
  color: #718096;
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 8px;
}

.trend-summary strong {
  color: #243b53;
  font-size: 1.2rem;
  font-weight: 700;
}

.tables-section {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e7edf4;
  border-radius: 22px;
  box-shadow: 0 18px 38px rgba(44, 52, 71, 0.08);
  padding: 24px;
}

.section-header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.section-title {
  color: #243b53;
  font-weight: 700;
  margin: 0;
}

.section-copy {
  color: #718096;
}

.tables-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}

.table-tab {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #52606d;
  cursor: pointer;
  font-weight: 700;
  padding: 10px 14px;
}

.table-tab.active {
  background: #8c1515;
  border-color: #8c1515;
  color: #fff;
}

.table-wrapper {
  overflow-x: auto;
}

.data-table {
  border-collapse: collapse;
  width: 100%;
}

.data-table th,
.data-table td {
  border-top: 1px solid #eef1f6;
  padding: 14px 16px;
  text-align: left;
}

.data-table th {
  color: #52606d;
  font-size: 12px;
  text-transform: uppercase;
}

.table-empty {
  color: #718096;
  padding: 24px;
  text-align: center;
}

@media (max-width: 1100px) {
  .metric-grid,
  .charts-grid {
    grid-template-columns: 1fr 1fr;
  }

  .country-detail-body {
    align-items: flex-start;
    flex-direction: column;
  }

  .country-stat-grid {
    min-width: 0;
    width: 100%;
  }
}

@media (max-width: 768px) {
  .metric-grid,
  .charts-grid,
  .trend-summary,
  .country-stat-grid {
    grid-template-columns: 1fr;
  }

  .charts-grid {
    gap: 16px;
    margin-bottom: 24px;
  }

  .chart-card-body {
    min-height: 240px;
    padding: 12px 20px 20px 20px;
  }

  .trend-summary {
    gap: 12px;
    padding: 0 20px 20px 20px;
  }

  .tables-section {
    padding: 18px;
  }
}
</style>
